(() => {
  if (customElements.get('cart-drawer-recommendations')) return;

  class CartDrawerRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.swatchClickHandler) {
        this.swatchClickHandler = (event) => this.handleSwatchClick(event);
        this.addEventListener('click', this.swatchClickHandler);
      }

      this.observeCartDrawer();
      this.loadWhenCartIsOpen();
    }

    disconnectedCallback() {
      if (this.swatchClickHandler) {
        this.removeEventListener('click', this.swatchClickHandler);
        this.swatchClickHandler = null;
      }
      
      if (this.cartModalObserver) {
        this.cartModalObserver.disconnect();
        this.cartModalObserver = null;
      }
    }


    getList() {
      return this.querySelector('[data-cart-drawer-recommendations-list]');
    }

    getIds(name) {
      return (this.dataset[name] || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
    }
    
    getCartModal() {
      return this.closest('xo-modal[xo-name="cart"]') || document.querySelector('xo-modal[xo-name="cart"]');
    }

    isCartOpen() {
      const cartModal = this.getCartModal();

      if (!cartModal) return false;

      return cartModal.hasAttribute('xo-active') && cartModal.getAttribute('xo-active') !== 'false';
    }

    getLoadKey() {
      return [
        this.dataset.productIds || '',
        this.dataset.cartProductIds || '',
        this.dataset.limit || '',
        this.dataset.intent || '',
      ].join('|');
    }

    observeCartDrawer() {
      const cartModal = this.getCartModal();

      if (!cartModal || this.cartModalObserver) return;

      this.cartModalObserver = new MutationObserver(() => {
        this.loadWhenCartIsOpen();
      });
      this.cartModalObserver.observe(cartModal, {
        attributes: true,
        attributeFilter: ['xo-active'],
      });
    }

    loadWhenCartIsOpen() {
      if (!this.isCartOpen()) return;

      const loadKey = this.getLoadKey();

      if (this.loadedKey === loadKey || this.loadingKey === loadKey) return;

      this.loadingKey = loadKey;
      this.load()
        .then(() => {
          this.loadedKey = loadKey;
        })
        .finally(() => {
          if (this.loadingKey === loadKey) {
            this.loadingKey = null;
          }
        });
    }

    getRecommendationsUrl(productId, limit) {
      const root = window.Shopify?.routes?.root || '/';
      const rawBase = this.dataset.recommendationsUrl || `${root}recommendations/products`;
      const base = rawBase.endsWith('.json') ? rawBase : `${rawBase}.json`;
      const url = new URL(base, window.location.origin);
      const safeLimit = Math.min(10, Math.max(1, Number(limit) || 1));

      url.searchParams.set('product_id', productId);
      url.searchParams.set('limit', safeLimit);
      url.searchParams.set('intent', this.dataset.intent || 'related');

      return url.toString();
    }

    async fetchRecommendations(productId, limit) {
      const safeLimit = Math.min(10, Math.max(1, Number(limit) || 1));
      const cacheKey = `cart-drawer-recommendations:${productId}:${safeLimit}:${this.dataset.intent || 'related'}`;
      const cached = this.readCache(cacheKey);

      if (cached) return cached;

      const response = await fetch(this.getRecommendationsUrl(productId, safeLimit), {
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) return [];

      const data = await response.json();
      const products = data.products || [];

      this.writeCache(cacheKey, products);

      return products;
    }

    readCache(key) {
      try {
        const cached = JSON.parse(sessionStorage.getItem(key) || 'null');

        if (!cached || Date.now() - cached.time > 300000) return null;

        return cached.products || null;
      } catch {
        return null;
      }
    }

    writeCache(key, products) {
      try {
        sessionStorage.setItem(
          key,
          JSON.stringify({
            time: Date.now(),
            products,
          })
        );
      } catch {
      }
    }

    getImage(product) {
      const image = product.featured_image || product.images?.[0];

      if (!image) return '';
      if (typeof image === 'string') return image;

      return image.src || image.url || '';
    }

    getVariantImage(variant) {
      const image = variant?.featured_image || variant?.image;

      if (!image) return '';
      if (typeof image === 'string') return image;

      return image.src || image.url || '';
    }

    getAvailableVariant(product) {
      return product.variants?.find((variant) => variant.available !== false) || product.variants?.[0];
    }

    getMarketKey() {
      const config = this.getMoneyConfig();
      return JSON.stringify([
        window.Shopify?.routes?.root || '/',
        config?.dataset.cartCurrency || window.Shopify?.currency?.active || '',
        config?.dataset.cartCountry || '',
      ]);
    }

    getMoneyConfig() {
      return (
        this.getCartModal()?.querySelector('[data-cart-money-sample]') ||
        document.querySelector('[data-cart-money-sample]')
      );
    }

    formatMoney(cents) {
      // The Recommendations API supplies amounts in currency subunits.
      const parsed =
        typeof cents === 'string' && cents.includes('.')
          ? Math.round(Number.parseFloat(cents) * 100)
          : Number(cents ?? 0);
      const value = Number.isFinite(parsed) ? parsed : 0;
      const config = this.getMoneyConfig();

      if (config?.dataset.cartMoneySample) {
        // Liquid renders this sample with the SAME filter as the cart total:
        // {{ 123456789 | money_with_currency }}.
        // Preserve its currency label, separators and decimal precision.
        const decoder = document.createElement('textarea');
        decoder.innerHTML = config.dataset.cartMoneySample;
        const sample = decoder.value;
        const match = sample.match(/[0-9](?:[0-9.,'’\u00a0\u202f ]*[0-9])?/);

        if (match) {
          const sampleNumber = match[0];
          const decimalMatch = sampleNumber.match(/([.,])89$/);
          const decimals = decimalMatch ? 2 : 0;
          const decimalSeparator = decimalMatch ? decimalMatch[1] : '';
          const sampleInteger = decimalMatch
            ? sampleNumber.slice(0, -3)
            : sampleNumber;
          const groups = sampleInteger.split(/[^0-9]+/);
          const groupSeparator = sampleInteger.match(/[^0-9]+/)?.[0] || '';
          const primarySize = groups[groups.length - 1].length;
          const secondarySize = groups.length > 2
            ? groups[groups.length - 2].length
            : primarySize;
          const fixed = (Math.abs(value) / 100).toFixed(decimals);
          let [integer, fraction] = fixed.split('.');

          if (groupSeparator) {
            const parts = [];
            let size = primarySize;

            while (integer.length > size) {
              parts.unshift(integer.slice(-size));
              integer = integer.slice(0, -size);
              size = secondarySize;
            }

            parts.unshift(integer);
            integer = parts.join(groupSeparator);
          }

          const amount =
            (value < 0 ? '-' : '') +
            integer +
            (decimals ? decimalSeparator + fraction : '');

          return (
            sample.slice(0, match.index) +
            amount +
            sample.slice(match.index + sampleNumber.length)
          );
        }
      }

      // Fallback for pages where the cart footer configuration is absent.
      const currency = config?.dataset.cartCurrency || window.Shopify?.currency?.active;

      if (currency) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || undefined, {
            style: 'currency',
            currency,
          }).format(value / 100);
        } catch {
          // Fall through to the existing theme formatter.
        }
      }

      if (typeof window.Shopify?.formatMoney === 'function') {
        const formatted = window.Shopify.formatMoney(value, this.dataset.moneyFormat);
        const container = document.createElement('div');
        container.innerHTML = formatted;
        return container.textContent || '';
      }

      return (value / 100).toFixed(2);
    }

    formatMoney(cents) {
      // The Recommendations API supplies amounts in currency subunits.
      const parsed =
        typeof cents === 'string' && cents.includes('.')
          ? Math.round(Number.parseFloat(cents) * 100)
          : Number(cents ?? 0);
      const value = Number.isFinite(parsed) ? parsed : 0;
      const config = this.getMoneyConfig();

      if (config?.dataset.cartMoneySample) {
        // Liquid renders this sample with the SAME filter as the cart total:
        // {{ 123456789 | money_with_currency }}.
        // Preserve its currency label, separators and decimal precision.
        const decoder = document.createElement('textarea');
        decoder.innerHTML = config.dataset.cartMoneySample;
        const sample = decoder.value;
        const match = sample.match(/[0-9](?:[0-9.,'’\u00a0\u202f ]*[0-9])?/);

        if (match) {
          const sampleNumber = match[0];
          const decimalMatch = sampleNumber.match(/([.,])89$/);
          const decimals = decimalMatch ? 2 : 0;
          const decimalSeparator = decimalMatch ? decimalMatch[1] : '';
          const sampleInteger = decimalMatch
            ? sampleNumber.slice(0, -3)
            : sampleNumber;
          const groups = sampleInteger.split(/[^0-9]+/);
          const groupSeparator = sampleInteger.match(/[^0-9]+/)?.[0] || '';
          const primarySize = groups[groups.length - 1].length;
          const secondarySize = groups.length > 2
            ? groups[groups.length - 2].length
            : primarySize;
          const fixed = (Math.abs(value) / 100).toFixed(decimals);
          let [integer, fraction] = fixed.split('.');

          if (groupSeparator) {
            const parts = [];
            let size = primarySize;

            while (integer.length > size) {
              parts.unshift(integer.slice(-size));
              integer = integer.slice(0, -size);
              size = secondarySize;
            }

            parts.unshift(integer);
            integer = parts.join(groupSeparator);
          }

          const amount =
            (value < 0 ? '-' : '') +
            integer +
            (decimals ? decimalSeparator + fraction : '');

          return (
            sample.slice(0, match.index) +
            amount +
            sample.slice(match.index + sampleNumber.length)
          );
        }
      }

      // Fallback for pages where the cart footer configuration is absent.
      const currency = config?.dataset.cartCurrency || window.Shopify?.currency?.active;

      if (currency) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || undefined, {
            style: 'currency',
            currency,
          }).format(value / 100);
        } catch {
          // Fall through to the existing theme formatter.
        }
      }

      if (typeof window.Shopify?.formatMoney === 'function') {
        const formatted = window.Shopify.formatMoney(value, this.dataset.moneyFormat);
        const container = document.createElement('div');
        container.innerHTML = formatted;
        return container.textContent || '';
      }

      return (value / 100).toFixed(2);
    }

    escapeHtml(value) {
      const span = document.createElement('span');
      span.textContent = value == null ? '' : String(value);
      return span.innerHTML;
    }

    getProductOptionNames(product) {
      return (product.options || []).map((option) => (typeof option === 'string' ? option : option.name)).filter(Boolean);
    }

    getVariantOptions(variant) {
      if (Array.isArray(variant?.options)) return variant.options;

      return [variant?.option1, variant?.option2, variant?.option3].filter((option) => option != null && option !== '');
    }

    getSwatchOptionIndex(product) {
      const optionNames = this.getProductOptionNames(product);
      const swatchName = (this.dataset.swatchOptionName || 'Color').trim().toLowerCase();
      const exactIndex = optionNames.findIndex((name) => String(name).trim().toLowerCase() === swatchName);

      if (exactIndex >= 0) return exactIndex;

      const colorIndex = optionNames.findIndex((name) => /color|colour|颜色|顏色/i.test(String(name)));
      return colorIndex >= 0 ? colorIndex : 0;
    }

    getVariantLabel(product, variant) {
      const options = this.getVariantOptions(variant);
      const optionIndex = this.getSwatchOptionIndex(product);
      const label = options[optionIndex] || variant?.public_title || variant?.title || '';

      return label === 'Default Title' ? '' : label;
    }

    getColorValue(label) {
      const value = String(label || '').trim().toLowerCase();
      const map = {
        black: '#232323',
        white: '#ffffff',
        gray: '#6b7280',
        grey: '#6b7280',
        silver: '#b7bcc1',
        titanium: '#6f7676',
        green: '#0d4336',
        blue: '#2f65a7',
        red: '#b4232a',
        yellow: '#d9a800',
        orange: '#c4652d',
        brown: '#7a5438',
        pink: '#d979a2',
        purple: '#7357a6',
      };

      if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value) || /^rgba?\(/i.test(value) || /^hsla?\(/i.test(value)) return value;
      if (map[value]) return map[value];
      if (value.includes('moss') || value.includes('green')) return '#3f6f4c';
      if (value.includes('titanium')) return '#6f7676';
      if (value.includes('grey') || value.includes('gray')) return '#6b7280';
      if (value.includes('black')) return '#232323';
      if (value.includes('white')) return '#ffffff';

      return '#d9d9d9';
    }

    renderSwatches(product, selectedVariant) {
      if (!product.variants || product.variants.length <= 1) return '';

      const productImage = this.getImage(product);
      const swatches = product.variants
        .map((variant) => {
          const label = this.getVariantLabel(product, variant) || variant.title || '';
          const variantImage = this.getVariantImage(variant);
          const image = variantImage || productImage;
          const current = String(variant.id) === String(selectedVariant?.id) ? 'true' : 'false';
          const disabled = variant.available === false ? 'true' : 'false';
          const price = variant.price ?? product.price;
          const swatchColor = this.getColorValue(label);
          const fill = `<span class="xo-cart-drawer-recommendations__swatch-fill"></span>`;
          const content = label ? fill : `<span class="xo-cart-drawer-recommendations__swatch-text">${this.escapeHtml(variant.title || 'SKU')}</span>`;

          return `
            <button
              class="xo-cart-drawer-recommendations__swatch"
              type="button"
              aria-label="${this.escapeHtml(label || variant.title)}"
              aria-current="${current}"
              aria-disabled="${disabled}"
              style="--swatch-color: ${swatchColor};"
              data-cart-drawer-recommendation-swatch
              data-variant-id="${this.escapeHtml(variant.id)}"
              data-variant-available="${variant.available !== false}"
              data-variant-price="${this.escapeHtml(price)}"
              data-variant-image="${this.escapeHtml(image)}"
            >
              ${content}
            </button>
          `;
        })
        .join('');

      return `<div class="xo-cart-drawer-recommendations__swatches" role="radiogroup">${swatches}</div>`;
    }

    renderButton(product, variant) {
      const available = product.available !== false && !!variant?.id && variant?.available !== false;
      const text = available ? this.dataset.addText : this.dataset.soldOutText;
      const disabled = available ? '' : ' xo-disabled';
      const textData = this.dataset.buttonEffect === 'slide-up' ? ` data-text="${this.escapeHtml(text)}"` : '';

      return `
  <xo-cart-add
    class="xo-cart-drawer-recommendations__action"
    xo-product-id="${this.escapeHtml(product.id)}"
    xo-variant-id="${this.escapeHtml(variant?.id || '')}"
    xo-section-id="cart-drawer-recommendations"
    ${disabled}
  >
    <button
      class="${this.escapeHtml(this.dataset.buttonClass || 'xo-btn xo-btn--primary xo-btn--xs xo-btn--block')} xo-cart-drawer-recommendations__add-button"
      type="button"
      aria-label="${this.escapeHtml(text)}"
    >
      <span class="xo-btn__content">
        <span class="xo-cart-drawer-recommendations__add-icon" aria-hidden="true">+</span>
        <span class="xo-btn__text"${textData}>${this.escapeHtml(text)}</span>
      </span>

      <span class="xo-btn__loading">
        <div class="xo-loader-3" style="--size: 8; --color: currentColor; --duration: 1000;">
          <div></div>
        </div>
      </span>
    </button>
  </xo-cart-add>
`;
    }

    renderProduct(product) {
      const variant = this.getAvailableVariant(product);
      const image = this.getImage(product);
      const productUrl = product.url || `/products/${product.handle || ''}`;
      const cardRadiusClass = this.dataset.enableRadius === 'true' ? ' xo-cart-drawer-recommendations__item--radius' : '';
      const imageRadiusClass = this.dataset.enableRadius === 'true' ? ' xo-cart-drawer-recommendations__image--radius' : '';

      return `
        <xo-product
          class="xo-cart-drawer-recommendations__item${cardRadiusClass}"
          xo-product-id="${this.escapeHtml(product.id)}"
          xo-section-id="cart-drawer-recommendations"
        >
          <a class="xo-cart-drawer-recommendations__image${imageRadiusClass}" href="${this.escapeHtml(productUrl)}" tabindex="-1" aria-hidden="true">
            ${
              image
                ? `<img src="${this.escapeHtml(image)}" alt="${this.escapeHtml(this.dataset.imageAlt || product.title)}" loading="lazy">`
                : '<div class="xo-cart-drawer-recommendations__empty-image"></div>'
            }
          </a>
          <div class="xo-cart-drawer-recommendations__content">
            <a class="xo-cart-drawer-recommendations__title" href="${this.escapeHtml(productUrl)}">${this.escapeHtml(product.title)}</a>
            <div class="xo-cart-drawer-recommendations__price" data-cart-drawer-recommendation-price>${this.escapeHtml(this.formatMoney(variant?.price ?? product.price))}</div>
            ${this.renderSwatches(product, variant)}
            ${this.renderButton(product, variant)}
          </div>
        </xo-product>
      `;
    }

    updateButton(productEl, available) {
      const action = productEl.querySelector('.xo-cart-drawer-recommendations__action');
      const text = available ? this.dataset.addText : this.dataset.soldOutText;

      if (!action) return;

      if (available) {
        action.removeAttribute('xo-disabled');
      } else {
        action.setAttribute('xo-disabled', '');
      }

      action.querySelectorAll('.xo-btn__text').forEach((textEl) => {
        textEl.textContent = text;
        if (textEl.hasAttribute('data-text')) {
          textEl.setAttribute('data-text', text);
        }
      });

      const button = action.querySelector('button');
      if (button) {
        button.setAttribute('aria-label', text);
      }
    }

    handleSwatchClick(event) {
      const swatch = event.target.closest('[data-cart-drawer-recommendation-swatch]');

      if (!swatch || !this.contains(swatch)) return;

      const productEl = swatch.closest('xo-product');
      const action = productEl?.querySelector('.xo-cart-drawer-recommendations__action');
      const price = productEl?.querySelector('[data-cart-drawer-recommendation-price]');
      const image = productEl?.querySelector('.xo-cart-drawer-recommendations__image img');
      const variantId = swatch.dataset.variantId;
      const available = swatch.dataset.variantAvailable !== 'false';

      if (!productEl || !action || !variantId) return;

      swatch.parentElement?.querySelectorAll('[data-cart-drawer-recommendation-swatch]').forEach((item) => {
        item.setAttribute('aria-current', 'false');
      });
      swatch.setAttribute('aria-current', 'true');
      action.setAttribute('xo-variant-id', variantId);

      if (price) {
        price.textContent = this.formatMoney(swatch.dataset.variantPrice);
      }

      if (image && swatch.dataset.variantImage) {
        image.setAttribute('src', swatch.dataset.variantImage);
      }

      this.updateButton(productEl, available);
    }

    async load() {
      const list = this.getList();
      const cartProductIds = new Set(this.getIds('cartProductIds'));
      const seedIds = [...new Set(this.getIds('productIds'))].slice(0, 5);
      const limit = Number(this.dataset.limit || 3);
      const fetchLimit = Math.min(10, limit + cartProductIds.size + 6);
      const seen = new Set();
      const products = [];
      const loadId = Date.now();

      this.loadId = loadId;

      if (!list || seedIds.length === 0) {
        this.hidden = true;
        return;
      }

      const addProducts = (items) => {
        items.forEach((product) => {
          const productId = String(product.id);

          if (cartProductIds.has(productId) || seen.has(productId)) return;
          if (product.available === false) return;

          seen.add(productId);
          products.push(product);
        });
      };

      const renderProducts = () => {
        if (this.loadId !== loadId) return;

        const visibleProducts = products.slice(0, limit);

        if (visibleProducts.length === 0) return;

        list.innerHTML = visibleProducts.map((product) => this.renderProduct(product)).join('');
        this.hidden = false;
      };

      try {
        const firstProducts = await this.fetchRecommendations(seedIds[0], fetchLimit);

        addProducts(firstProducts);
        renderProducts();

        if (products.length >= limit || seedIds.length === 1) {
          if (products.length === 0) this.hidden = true;
          return;
        }

        await Promise.allSettled(
          seedIds.slice(1).map(async (id) => {
            const nextProducts = await this.fetchRecommendations(id, fetchLimit);

            addProducts(nextProducts);
            renderProducts();
          })
        );

        if (products.length === 0) {
          this.hidden = true;
        }
      } catch {
        this.hidden = true;
      }
    }
  }

  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
})();
